/**
 * Admin route — FWA-PWA-033 + REPORT-045 (botón reportería trimestral).
 *
 * Tab-based layout: Audit log + RTBF requests. Admin-only.
 * Top-right action: "Generar reporte trimestral" (FWA-REPORT-045 sprint-21).
 */
import { useState } from 'react';
import { Card, CardBody } from '../../components/ui/Card.js';
import { useAuth } from '../../contexts/AuthContext.js';
import { AuditLogView } from './AuditLogView.js';
import { RtbfRequestsView } from './RtbfRequestsView.js';

type Tab = 'audit' | 'rtbf';

/**
 * Computa quarter actual (YYYY-Q[1-4]) basado en fecha actual.
 * Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec.
 */
function computeCurrentQuarter(now: Date = new Date()): string {
  const month = now.getMonth(); // 0-11
  const q = Math.floor(month / 3) + 1;
  return `${now.getFullYear()}-Q${q}`;
}

export function Admin() {
  const { state } = useAuth();
  const isAdmin =
    state.status === 'authenticated' &&
    (state.user.rol === 'admin-distrital' || state.user.rol === 'admin-nacional');

  const [tab, setTab] = useState<Tab>('audit');

  if (state.status === 'authenticated' && !isAdmin) {
    return (
      <Card>
        <CardBody>
          <h1 className="text-2xl font-semibold text-gray-900">Acceso restringido</h1>
          <p className="mt-2 text-gray-600">
            Esta vista es solo para administradores del distrito.
          </p>
        </CardBody>
      </Card>
    );
  }

  const currentQuarter = computeCurrentQuarter();
  const reportUrl = `/v1/reports/trimestre/${currentQuarter}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Admin panel</h1>
        <a
          href={reportUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-md bg-distrito-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-distrito-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-distrito-600"
          title={`Genera reporte trimestral HTML para ${currentQuarter}. Imprime/guarda como PDF desde el browser para enviar a coordinación distrital.`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5 4a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2H5zm1 4a1 1 0 100 2h8a1 1 0 100-2H6zm0 4a1 1 0 100 2h8a1 1 0 100-2H6z"
              clipRule="evenodd"
            />
          </svg>
          Generar reporte trimestral ({currentQuarter})
        </a>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-1" role="tablist">
          <TabButton active={tab === 'audit'} onClick={() => setTab('audit')}>
            Audit log
          </TabButton>
          <TabButton active={tab === 'rtbf'} onClick={() => setTab('rtbf')}>
            RTBF requests
          </TabButton>
        </nav>
      </div>

      {tab === 'audit' && <AuditLogView />}
      {tab === 'rtbf' && <RtbfRequestsView />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
        active
          ? 'border-distrito-600 text-distrito-700'
          : 'border-transparent text-gray-600 hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}
